import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";

interface EmailVerificationCodeInputProps {
  email: string;
  userId: string;
  onSuccess: () => void;
  onResend: () => void;
}

export function EmailVerificationCodeInput({
  email,
  userId,
  onSuccess,
  onResend
}: EmailVerificationCodeInputProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleVerify = async (value: string) => {
    if (value.length !== 6) return;
    
    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-email-code', {
        body: { email, code: value }
      });

      if (error) throw error;
      
      if (data.error) {
        toast.error(data.error);
        setCode("");
      } else {
        toast.success(t("auth.emailVerified"));
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.message || t("auth.verificationFailed"));
      setCode("");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    
    setCanResend(false);
    setResendCooldown(60);
    
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      await onResend();
      toast.success(t("auth.codeSent"));
    } catch (error) {
      toast.error(t("auth.resendFailed"));
      setCanResend(true);
      setResendCooldown(0);
      clearInterval(interval);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <p className="text-sm text-muted-foreground">
          {t("auth.enterCodeSentTo")}
        </p>
        <p className="font-medium">{email}</p>
      </div>

      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          onComplete={handleVerify}
          disabled={isVerifying}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {isVerifying && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t("auth.verifying")}
        </div>
      )}

      <div className="text-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResend}
          disabled={!canResend}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          {canResend 
            ? t("auth.resendCode")
            : `${t("auth.resendIn")} ${resendCooldown}s`
          }
        </Button>
      </div>

      <p className="text-xs text-center text-muted-foreground">
        {t("auth.codeExpiresIn")} 10 {t("auth.minutes")}
      </p>
    </div>
  );
}
